import React, { useState, useEffect, Suspense, useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import BASE_URL from "api/Api";
// MUI
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard Components
import MDBox from "components/MDBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Routes
import routes from "routes";
import "bootstrap/dist/js/bootstrap.bundle.min.js";

// Context
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

// Images
import brandWhite from "assets/images/logos/logo.jpeg";
import brandDark from "assets/images/logos/logo.jpeg";

// USER SIDE
import UserLayout from "layouts/user_layout/UserLayout";
import PageLoader from "common_component/PageLoader";
const Home = React.lazy(() => import("pages/user/Home"));
const About = React.lazy(() => import("pages/user/About"));
const Schemes = React.lazy(() => import("pages/user/Schemes"));
const Gallery = React.lazy(() => import("pages/user/Gallery"));
const Login = React.lazy(() => import("common_component/Login"));
const RegisterForm = React.lazy(() => import("common_component/RegistrationForm"));
const SchemeDetails = React.lazy(() =>
  import("components/user_components/home_components/SchemeDetails")
);
const JantaDarbarDetails = React.lazy(() =>
  import("components/user_components/home_components/JanataDarbarDetails")
);
const ComplaintPage = React.lazy(() =>
  import("components/user_components/complaint_components/ComplaintPage")
);
const Profile = React.lazy(() => import("./common_component/ProfilePage"));
const UserProfileUpdate = React.lazy(() =>
  import("./common_component/UserProfileUpdate")
);
const GetComplaints = React.lazy(() =>
  import("components/user_components/complaint_components/GetComplaints")
);
const AllUserComplaints = React.lazy(() =>
  import("components/user_components/complaint_components/AllUserComplaint")
);
const Suggestions = React.lazy(() =>
  import("components/user_components/suggestions/AddSuggestions")
);


// ADMIN SIDE

export default function App() {
  const user = JSON.parse(localStorage.getItem("user"));
  const role = localStorage.getItem("role");

  // Display party logo image for Worker / Leader / Admin; don't display logo image for Super Admin
  const partyLogo = role === "Super_Admin"
    ? null
    : (user?.party_symbol ? `${BASE_URL}/${user.party_symbol}` : null);

  const partyName = role === "Super_Admin"
    ? "Super Admin Portal"
    : (user?.short_name || "Political SAAS");

  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  const hasPermission = (userPermissions, required) => {
    if (!required) return true;
    if (!userPermissions || !Array.isArray(userPermissions)) return false;
    if (userPermissions.includes(required)) return true;

    const alternates = [];
    if (required.startsWith("manage_")) {
      const base = required.replace("manage_", "");
      alternates.push(`view_${base}`, `add_${base}`, `edit_${base}`);
    } else if (required.startsWith("view_")) {
      const base = required.replace("view_", "");
      alternates.push(`manage_${base}`, `add_${base}`, `edit_${base}`);
    } else if (required.startsWith("add_")) {
      const base = required.replace("add_", "");
      alternates.push(`manage_${base}`, `view_${base}`, `edit_${base}`);
    }

    return alternates.some((alt) => userPermissions.includes(alt));
  };

  //  FILTER ADMIN ROUTES DYNAMICALLY 
  const getFilteredRoutes = (allRoutes) => {
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    return allRoutes
      .map((route) => {
        if (route.collapse) {
          const filteredCollapse = getFilteredRoutes(route.collapse);
          if (filteredCollapse.length === 0) return null;
          return { ...route, collapse: filteredCollapse };
        }

        if (route.type === "title" || route.type === "divider") {
          return route;
        }

        if (route.key === "dashboard") return route;

        if (route.requiredPermission) {
          return hasPermission(permissions, route.requiredPermission)
            ? route
            : null;
        }

        return route;
      })
      .filter(Boolean);
  };

  const filteredRoutes = getFilteredRoutes(routes);

  //  RTL CACHE 
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  //  SIDENAV EVENTS 
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  //  ADMIN ROUTE GENERATOR 
  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }
      if (route.route) {
        return (
          <Route
            path={route.route}
            element={route.component}
            key={route.key || route.route || index}
          />
        );
      }
      return null;
    });

  const isAuthPage =
    pathname === "/login" ||
    pathname === "/register" ||
    pathname === "/update_profile" ||
    pathname === "/authentication/sign-in" ||
    pathname.startsWith("/admin/login") ||
    pathname.startsWith("/admin/signin") ||
    pathname.startsWith("/signin");

  const isAdminRoute =
    !pathname.startsWith("/authentication") &&
    !pathname.startsWith("/login") &&
    !pathname.startsWith("/register") &&
    !pathname.startsWith("/update_profile") &&
    !pathname.startsWith("/profile") &&
    !pathname.startsWith("/about_us") &&
    !pathname.startsWith("/schemes") &&
    !pathname.startsWith("/gallery") &&
    !pathname.startsWith("/complaint") &&
    !pathname.startsWith("/janta-darbar") &&
    !pathname.startsWith("/all_user_complaints") &&
    !pathname.startsWith("/user_complaints") &&
    !pathname.startsWith("/admin/login") &&
    !pathname.startsWith("/admin/signin") &&
    !pathname.startsWith("/signin") &&
    pathname !== "/";
  //  MAIN RETURN 
  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />

        {/* ADMIN SIDEBAR */}
        {!isAuthPage && isAdminRoute && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={
                partyLogo
              }
              brandName={partyName}
              routes={filteredRoutes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
          </>
        )}

        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ================= USER ROUTES ================= */}
            <Route element={<UserLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about_us" element={<About />} />
              <Route path="/schemes" element={<Schemes />} />
              <Route path="/schemes/:id" element={<SchemeDetails />} />
              <Route path="/janta-darbar" element={<JantaDarbarDetails />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/complaint" element={<ComplaintPage />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/all_user_complaints" element={<GetComplaints />} />
              <Route path="/user_complaints" element={<AllUserComplaints />} />
              {/* <Route path="/suggestions" element={<Suggestions />} /> */}
            </Route>
            <Route path="login" element={<Login />} />
            <Route path="register" element={<RegisterForm />} />
            <Route path="/update_profile" element={<UserProfileUpdate />} />


            {/*  ADMIN ROUTES & ALIASES  */}
            <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
            <Route path="/admin/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/admin/dashboard" element={<Navigate to="/dashboard" replace />} />
            <Route path="/admin-panel" element={<Navigate to="/dashboard" replace />} />
            <Route path="/adminpanel" element={<Navigate to="/dashboard" replace />} />
            <Route path="/admin-dashboard" element={<Navigate to="/dashboard" replace />} />
            <Route path="/admin/login" element={<Navigate to="/authentication/sign-in" replace />} />
            <Route path="/admin/signin" element={<Navigate to="/authentication/sign-in" replace />} />
            <Route path="/signin" element={<Navigate to="/authentication/sign-in" replace />} />
            {getRoutes(routes)}

            {/*  FALLBACK  */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Suspense>

      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />

      {!isAuthPage && isAdminRoute && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={
              partyLogo
            }
            brandName={partyName}
            routes={filteredRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
        </>
      )}


      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ================= USER ROUTES ================= */}

          <Route element={<UserLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about_us" element={<About />} />
            <Route path="/schemes" element={<Schemes />} />
            <Route path="/schemes/:id" element={<SchemeDetails />} />
            <Route path="/janta-darbar" element={<JantaDarbarDetails />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/complaint" element={<ComplaintPage />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/all_user_complaints" element={<GetComplaints />} />
            <Route path="/user_complaints" element={<AllUserComplaints />} />
            {/* <Route path="/suggestions" element={<Suggestions />} /> */}
          </Route>

          <Route path="login" element={<Login />} />
          <Route path="register" element={<RegisterForm />} />
          <Route path="/update_profile" element={<UserProfileUpdate />} />
          {/*  ADMIN ROUTES & ALIASES  */}
          <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
          <Route path="/admin/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/admin/dashboard" element={<Navigate to="/dashboard" replace />} />
          <Route path="/admin-panel" element={<Navigate to="/dashboard" replace />} />
          <Route path="/adminpanel" element={<Navigate to="/dashboard" replace />} />
          <Route path="/admin-dashboard" element={<Navigate to="/dashboard" replace />} />
          <Route path="/admin/login" element={<Navigate to="/authentication/sign-in" replace />} />
          <Route path="/admin/signin" element={<Navigate to="/authentication/sign-in" replace />} />
          <Route path="/signin" element={<Navigate to="/authentication/sign-in" replace />} />
          {getRoutes(routes)}

          {/*  FALLBACK  */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>

    </ThemeProvider>
  );
}
